import { axiosInstance } from '@/plugins/axios'
import { WALLET_TRANSACTION_FILTER } from '@/components/appDriver/wallet/types'
import type {
  WalletBankTransferRequest,
  WalletBankTransferResult,
  WalletSummary,
  WalletTransaction,
  WalletTransactionDirection,
  WalletTransactionFilter,
} from '@/components/appDriver/wallet/types'

type RawWalletDetail = Record<string, any>
type RawWalletTransaction = Record<string, any>

const WALLET_DETAIL_ENDPOINT = '/webapp/driver/wallet/detail'
const WALLET_TRANSACTIONS_ENDPOINT = '/webapp/driver/wallet/transactions'
const WALLET_BANK_TRANSFER_ENDPOINT = '/webapp/driver/bank-transfer'

const transactionTypeByFilter: Record<WalletTransactionFilter, number> = {
  [WALLET_TRANSACTION_FILTER.DEBIT]: 1,
  [WALLET_TRANSACTION_FILTER.CREDIT]: 2,
  [WALLET_TRANSACTION_FILTER.ALL]: 3,
}

function getStringValue(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') {
      return value
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return `${value}`
    }
  }

  return undefined
}

function getNumberValue(...values: unknown[]): number {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }

    if (typeof value === 'string') {
      const normalizedValue = Number(value.replace(/,/g, ''))

      if (Number.isFinite(normalizedValue)) {
        return normalizedValue
      }
    }
  }

  return 0
}

function getBooleanValue(...values: unknown[]): boolean {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value === 1
    }

    if (typeof value === 'string') {
      const normalizedValue = value.trim().toLowerCase()

      if (normalizedValue === 'true' || normalizedValue === '1') {
        return true
      }

      if (normalizedValue === 'false' || normalizedValue === '0') {
        return false
      }
    }
  }

  return false
}

function getObjectValue(...values: unknown[]): Record<string, any> | null {
  for (const value of values) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, any>
    }
  }

  return null
}

function extractDetailPayload(response: any): RawWalletDetail | null {
  const candidates = [
    response?.data?.data,
    response?.data?.wallet,
    response?.data,
    response?.wallet,
    response,
  ]

  for (const candidate of candidates) {
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      return candidate
    }
  }

  return null
}

function mapWalletSummary(rawDetail: RawWalletDetail): WalletSummary {
  return {
    balance: getNumberValue(rawDetail.walletBalance, rawDetail.wallet_balance, rawDetail.balance),
    currency: 'LAK',
    currencySymbol: getStringValue(rawDetail.currencySymbol, rawDetail.currency_symbol) ?? '₭',
    currencyAbbr: getStringValue(rawDetail.currencyAbbr, rawDetail.currency_abbr) ?? null,
    softLimit: getNumberValue(rawDetail.walletSoftLimit, rawDetail.wallet_soft_limit, rawDetail.softLimit, rawDetail.soft_limit),
    hardLimit: getNumberValue(rawDetail.walletHardLimit, rawDetail.wallet_hard_limit, rawDetail.hardLimit, rawDetail.hard_limit),
    updatedAt: getStringValue(rawDetail.updatedAt, rawDetail.updated_at) ?? new Date().toISOString(),
    isBankDetailsEnable: getBooleanValue(rawDetail.isBankDetailsEnable, rawDetail.is_bank_details_enable),
    accountNumber: getStringValue(rawDetail.accountNumber, rawDetail.account_number) ?? null,
    accountHolderName: getStringValue(rawDetail.accountHolderName, rawDetail.account_holder_name) ?? null,
  }
}

function normalizeDirection(
  rawTransaction: RawWalletTransaction,
  amount: number,
  fallbackDirection?: WalletTransactionDirection,
): WalletTransactionDirection {
  const rawDirection = `${rawTransaction.direction ?? rawTransaction.transaction_direction ?? rawTransaction.type ?? ''}`.toLowerCase()
  const rawTransactionType = Number(rawTransaction.transaction_type)

  if (rawDirection === 'credit' || rawDirection === 'cr' || rawTransactionType === 2) {
    return 'credit'
  }

  if (rawDirection === 'debit' || rawDirection === 'dr' || rawTransactionType === 1) {
    return 'debit'
  }

  if (fallbackDirection) {
    return fallbackDirection
  }

  return amount >= 0 ? 'credit' : 'debit'
}

function extractListPayload(response: any): RawWalletTransaction[] {
  const candidates = [
    response?.data?.data?.transactions,
    response?.data?.data?.items,
    response?.data?.data?.rows,
    response?.data?.data?.results,
    response?.data?.data?.list,
    response?.data?.data?.records,
    response?.data?.data,
    response?.data?.transactions,
    response?.data?.items,
    response?.data?.rows,
    response?.data?.results,
    response?.data?.list,
    response?.data?.records,
    response?.data,
    response?.result?.transactions,
    response?.result?.items,
    response?.result?.rows,
    response?.result?.results,
    response?.result?.list,
    response?.result?.records,
    response?.result,
    response?.results,
    response?.transactions,
    response?.items,
    response?.rows,
    response?.list,
    response?.records,
    response,
  ]

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.filter((item) => item && typeof item === 'object')
    }
  }

  return findTransactionList(response)
}

function findTransactionList(value: unknown): RawWalletTransaction[] {
  if (!value || typeof value !== 'object') {
    return []
  }

  if (Array.isArray(value)) {
    return value.filter((item) => item && typeof item === 'object')
  }

  for (const childValue of Object.values(value)) {
    const transactions = findTransactionList(childValue)

    if (transactions.length > 0) {
      return transactions
    }
  }

  return []
}

function mapWalletTransaction(
  rawTransaction: RawWalletTransaction,
  fallbackDirection?: WalletTransactionDirection,
): WalletTransaction {
  const rawAmount = getNumberValue(rawTransaction.amount, rawTransaction.value, rawTransaction.transaction_amount)
  const direction = normalizeDirection(rawTransaction, rawAmount, fallbackDirection)
  const amount = direction === 'debit' ? -Math.abs(rawAmount) : Math.abs(rawAmount)
  const createdAt = getStringValue(
    rawTransaction.date_time,
    rawTransaction.createdAt,
    rawTransaction.created_at,
    rawTransaction.transaction_date,
    rawTransaction.date,
  ) ?? new Date(0).toISOString()

  return {
    id: getStringValue(rawTransaction.id, rawTransaction._id, rawTransaction.transaction_id, rawTransaction.trans_id, rawTransaction.uuid) ?? createdAt,
    bookingId: getStringValue(
      rawTransaction.bookingId,
      rawTransaction.booking_id,
      rawTransaction.tripId,
      rawTransaction.trip_id,
      rawTransaction.bid,
    ) ?? null,
    title: getStringValue(rawTransaction.title, rawTransaction.name, rawTransaction.description, rawTransaction.remark, rawTransaction.transaction_name) ?? '-',
    amount,
    direction,
    createdAt,
  }
}

export async function getWalletTransactions(
  filter: WalletTransactionFilter,
  options: { skip?: number, lan?: string } = {},
): Promise<WalletTransaction[]> {
  const { data } = await axiosInstance.get(WALLET_TRANSACTIONS_ENDPOINT, {
    params: {
      transaction_type: transactionTypeByFilter[filter],
      skip: options.skip ?? 0,
      lan: options.lan ?? 'lo',
    },
  })

  const fallbackDirection = filter === WALLET_TRANSACTION_FILTER.ALL ? undefined : filter

  return extractListPayload(data).map((transaction) => mapWalletTransaction(transaction, fallbackDirection))
}

export async function getWalletDetail(options: { lan?: string } = {}): Promise<WalletSummary> {
  const { data } = await axiosInstance.get(WALLET_DETAIL_ENDPOINT, {
    params: {
      lan: options.lan ?? 'lo',
    },
  })

  const walletDetail = extractDetailPayload(data)

  if (!walletDetail) {
    throw new Error('Cannot load wallet detail')
  }

  return mapWalletSummary(walletDetail)
}

export async function payDueToLocaByBankTransfer(
  request: WalletBankTransferRequest,
): Promise<WalletBankTransferResult> {
  const { data } = await axiosInstance.post(WALLET_BANK_TRANSFER_ENDPOINT, request)

  return mapWalletBankTransferResult(request, data)
}

export async function payoutToDriverByBankTransfer(
  request: WalletBankTransferRequest,
): Promise<WalletBankTransferResult> {
  const { data } = await axiosInstance.post(WALLET_BANK_TRANSFER_ENDPOINT, request)

  return mapWalletBankTransferResult(request, data)
}

function mapWalletBankTransferResult(
  request: WalletBankTransferRequest,
  response: any,
): WalletBankTransferResult {
  const result = getObjectValue(response?.data, response) ?? {}

  return {
    idempotencyKey: request.idempotency_key,
    xref: getStringValue(result.xref) ?? null,
    duplicate: getBooleanValue(result.duplicate),
    direction: getStringValue(result.direction) ?? null,
    provider: getStringValue(result.provider) ?? null,
    amount: getNumberValue(result.amount),
    currency: getStringValue(result.currency) ?? 'LAK',
    bankStatus: getStringValue(result.bank_status, result.bankStatus) ?? null,
    walletStatus: getStringValue(result.wallet_status, result.walletStatus) ?? null,
    overallStatus: getStringValue(result.overall_status, result.overallStatus) ?? null,
    bankRefNo: getStringValue(result.bank_ref_no, result.bankRefNo) ?? null,
    requestedAt: getStringValue(result.requested_at, result.requestedAt) ?? null,
    message: getStringValue(response?.message, result.message) ?? null,
  }
}
